import { Link } from "react-router-dom";
import { CalendarDays, Phone, MessageCircle, ChevronRight } from "lucide-react";
import { business, whatsappLink, routes } from "@/lib/site";
import { useBooking } from "@/components/BookingContext";

// Reusable orange primary "Book Appointment" button
export const BookButton = ({ label = "Book an Appointment", className = "", testId = "book-btn" }) => {
  const { openBooking } = useBooking();
  return (
    <button
      onClick={openBooking}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-base font-bold text-white shadow-sm hover:bg-brand-orange-dark hover:-translate-y-0.5 transition-[transform,background-color] duration-200 ${className}`}
    >
      <CalendarDays className="h-5 w-5" /> {label}
    </button>
  );
};

export const CallButton = ({ className = "", testId = "call-btn" }) => (
  <a
    href={`tel:${business.phoneTel}`}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-7 py-3.5 text-base font-bold text-white hover:bg-white hover:text-navy hover:border-white transition-colors ${className}`}
  >
    <Phone className="h-5 w-5" /> Call Now
  </a>
);

export const WhatsAppButton = ({ className = "", testId = "whatsapp-btn", label = "WhatsApp Us" }) => (
  <a
    href={whatsappLink()}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-base font-bold text-white hover:brightness-95 transition-[filter] ${className}`}
  >
    <MessageCircle className="h-5 w-5" /> {label}
  </a>
);

// Section heading with eyebrow label
export const SectionHeading = ({ eyebrow, title, sub, center = false, light = false }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl mb-12`}>
    {eyebrow && (
      <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
        {eyebrow}
      </span>
    )}
    <h2 className={`font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] ${light ? "text-white" : "text-navy"}`}>
      {title}
    </h2>
    {sub && (
      <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-white/70" : "text-ink"}`}>
        {sub}
      </p>
    )}
  </div>
);

// Final CTA band used across pages
export const FinalCTA = ({
  title = "Take the First Step Towards Better Mobility",
  sub = "Book a consultation with Dr. Satyam Jaiswal and start your personalized path to pain relief and better movement.",
}) => (
  <section className="relative overflow-hidden bg-navy" data-testid="final-cta">
    <div className="absolute inset-0 hero-glow" />
    <div className="absolute inset-0 dotted-grid opacity-40" />
    <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24 text-center">
      <h2 className="font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
        {title}
      </h2>
      <p className="mt-5 text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{sub}</p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
        <BookButton testId="cta-book-btn" />
        <CallButton testId="cta-call-btn" />
        <WhatsAppButton testId="cta-whatsapp-btn" />
      </div>
    </div>
  </section>
);

// Breadcrumb
export const Breadcrumb = ({ items }) => (
  <nav className="flex items-center gap-1.5 text-sm text-white/60" data-testid="breadcrumb">
    <Link to={routes.home} className="hover:text-brand-orange transition-colors">Home</Link>
    {items.map((it) => (
      <span key={it.label} className="flex items-center gap-1.5">
        <ChevronRight className="h-3.5 w-3.5" />
        {it.to ? (
          <Link to={it.to} className="hover:text-brand-orange transition-colors">{it.label}</Link>
        ) : (
          <span className="text-white">{it.label}</span>
        )}
      </span>
    ))}
  </nav>
);

// Dark page hero used on inner pages
export const PageHero = ({ eyebrow, title, sub, breadcrumb }) => (
  <section className="relative overflow-hidden bg-navy" data-testid="page-hero">
    <div className="absolute inset-0 hero-glow" />
    <div className="absolute inset-0 dotted-grid opacity-30" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-16 lg:pb-20">
      {breadcrumb && <div className="mb-6"><Breadcrumb items={breadcrumb} /></div>}
      {eyebrow && (
        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
          {eyebrow}
        </span>
      )}
      <h1 className="font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
        {title}
      </h1>
      {sub && <p className="mt-5 text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">{sub}</p>}
    </div>
  </section>
);
